import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { DefinitionList, ResourceState, SectionCard, StatusBadge } from '../../../design-system';
import type { PeopleProfilePort } from './port';
import type { PersonHistoryEntry } from './model';

const labels = { NAME: '이름', HOME_CONTACT: '개인 연락처', WORK_CONTACT: '업무 연락처', STAFFING: '인사 발령' };
const timestamp = new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', dateStyle: 'medium', timeStyle: 'short' });
function valueLabel(value: string) {
  const statuses: Record<string,string> = { ACTIVE: '재직', ON_LEAVE: '휴직', ENDED: '종료', SUSPENDED: '휴직', PRE_HIRE: '입사 전' };
  if (statuses[value]) return statuses[value];
  if (value.startsWith('[')) { try { const parsed: unknown = JSON.parse(value); if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) return parsed.join(' '); } catch { /* plain text */ } }
  return value;
}
export function PersonChangeHistory({ personId, port }: { personId: string; port: PeopleProfilePort }) {
  const [entries, setEntries] = useState<PersonHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    void port.listPersonHistory?.(personId, offset).then((result) => {
      if (!active) return;
      setEntries((current) => offset === 0 ? result.data : [...current, ...result.data.filter((item) => !current.some((entry) => entry.id === item.id))]);
      setNextOffset(offset + result.data.length);
      setHasMore(result.hasMore); setLoading(false);
    }, () => { if (active) { setError('변경 이력을 불러오지 못했습니다.'); setLoading(false); } });
    return () => { active = false; };
  }, [personId, port, offset, retry]);
  const grouped = new Map<string, { entry: PersonHistoryEntry; before: Set<string>; after: Set<string> }>();
  for (const entry of entries) {
    const key = `${entry.eventId}:${entry.category}:${entry.field}`;
    const item = grouped.get(key) ?? { entry, before: new Set<string>(), after: new Set<string>() };
    if (entry.before) item.before.add(valueLabel(entry.before));
    if (entry.after) item.after.add(valueLabel(entry.after));
    grouped.set(key,item);
  }
  return <SectionCard title="변경 이력" bodyPadding="standard">
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>확정된 정보의 변경 기록입니다. 현재 조회 권한이 있는 이름·연락처·인사 발령만 표시하며, 시간은 한국 시간 기준입니다.</Typography>
    <ResourceState embedded density="compact" loading={loading && !entries.length} error={!entries.length && error ? error : undefined} empty={!loading && !error && !entries.length} emptyTitle="확정된 정보 변경 이력이 없습니다." onRetry={() => setRetry((current) => current + 1)}>
      <Stack spacing={2}>{[...grouped.entries()].map(([key,{ entry, before, after }]) => <Box key={key}>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 1 }}><StatusBadge tone="neutral" label={labels[entry.category]} /><Typography variant="body2" sx={{ fontWeight: 700 }}>{entry.field}</Typography></Stack>
        <Typography variant="caption" color="text.secondary">{timestamp.format(new Date(entry.occurredAt))} · {entry.actor}{entry.effectiveOn ? ` · 적용일 ${entry.effectiveOn}` : ''}</Typography>
        <DefinitionList items={[["변경 전", [...before].join(' · ') || '없음'], ["변경 후", [...after].join(' · ') || '없음'], ...(entry.reason ? [["사유",entry.reason] as [string,string]] : [])]} />
      </Box>)}</Stack>
      {error && <Alert severity="error" action={<Button onClick={() => setRetry((current) => current + 1)}>다시 시도</Button>}>{error}</Alert>}
      {hasMore && !error && <Button variant="weak" loading={loading} sx={{ mt: 2 }} onClick={() => setOffset(nextOffset)}>이전 변경 더 보기</Button>}
    </ResourceState>
  </SectionCard>;
}
